import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTournament } from "@/lib/TournamentContext";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowBigUp, ArrowBigDown, MessageSquare, Send, Hash, Users, Bot, Flame, Clock, Trash2 } from "lucide-react";
import { MentionTextInput, renderWithMentions } from "@/components/app/MentionTextInput";
import { useAuth } from "@/lib/AuthContext";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const sortPosts = (list, sort) =>
  list.slice().sort((a, b) =>
    sort === "new"
      ? new Date(b.created_at) - new Date(a.created_at)
      : (b.score - a.score) || (new Date(b.created_at) - new Date(a.created_at))
  );

// Plain (non-component) recursive flatten → avoids recursive-component pitfalls.
const flattenTree = (childrenMap, sort) => {
  const out = [];
  const walk = (parentKey, depth) => {
    sortPosts(childrenMap[parentKey] || [], sort).forEach((p) => {
      out.push({ post: p, depth });
      walk(p.post_id, depth + 1);
    });
  };
  walk("__root__", 0);
  return out;
};

function PostRow({ item, onVote, onReply, onDelete, canDelete, members, memberNames }) {
  const post = item.post;
  const depth = item.depth;
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState("");

  const submitReply = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!replyText.trim()) return;
    await onReply(post.post_id, replyText.trim());
    setReplyText("");
    setReplying(false);
  };

  const scoreColor = post.score > 0 ? "text-primary-accent" : post.score < 0 ? "text-blue-400" : "text-zinc-400";

  return (
    <div style={{ marginLeft: depth > 0 ? Math.min(depth, 6) * 16 : 0 }} className={depth > 0 ? "border-l border-white/10 pl-3" : ""}>
      <div className={`flex gap-2 py-2.5 ${post.is_system ? "bg-primary-accent/5 border border-primary-accent/30 px-3" : ""}`} data-testid={`chat-post-${post.post_id}`}>
        {post.is_system ? (
          <div className="pt-1 text-primary-accent select-none"><Bot size={20} /></div>
        ) : (
          <div className="flex flex-col items-center gap-0.5 pt-0.5 select-none">
            <button
              data-testid={`chat-upvote-${post.post_id}`}
              onClick={() => onVote(post, post.my_vote === 1 ? 0 : 1)}
              className={`transition-colors ${post.my_vote === 1 ? "text-primary-accent" : "text-zinc-500 hover:text-white"}`}
            >
              <ArrowBigUp size={20} fill={post.my_vote === 1 ? "currentColor" : "none"} />
            </button>
            <span className={`font-display text-sm tabular-nums ${scoreColor}`}>{post.score}</span>
            <button
              data-testid={`chat-downvote-${post.post_id}`}
              onClick={() => onVote(post, post.my_vote === -1 ? 0 : -1)}
              className={`transition-colors ${post.my_vote === -1 ? "text-blue-400" : "text-zinc-500 hover:text-white"}`}
            >
              <ArrowBigDown size={20} fill={post.my_vote === -1 ? "currentColor" : "none"} />
            </button>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 text-xs">
            {post.is_system ? (
              <span className="flex items-center gap-1 text-primary-accent font-display tracking-wider uppercase"><Bot size={12} /> {post.user_name}</span>
            ) : (
              <span className="font-display tracking-wider uppercase text-zinc-200">{post.user_name}</span>
            )}
            <span className="text-zinc-600">· {timeAgo(post.created_at)}</span>
          </div>
          <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{renderWithMentions(post.content, memberNames)}</p>
          {(!post.is_system || canDelete) && (
            <div className="flex items-center gap-3 mt-1">
              {!post.is_system && (
                <button
                  data-testid={`chat-reply-btn-${post.post_id}`}
                  onClick={() => setReplying((r) => !r)}
                  className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-primary-accent flex items-center gap-1"
                >
                  <MessageSquare size={11} /> Reply
                </button>
              )}
              {canDelete && (
                <button
                  data-testid={`chat-delete-btn-${post.post_id}`}
                  onClick={() => onDelete(post)}
                  className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-primary-accent flex items-center gap-1"
                >
                  <Trash2 size={11} /> Delete
                </button>
              )}
            </div>
          )}
          {replying && (
            <form onSubmit={submitReply} className="flex gap-2 mt-2">
              <MentionTextInput
                value={replyText}
                onChange={setReplyText}
                onEnter={submitReply}
                members={members}
                autoFocus
                testid={`chat-reply-input-${post.post_id}`}
                placeholder="Write a reply… use @ to mention"
              />
              <button data-testid={`chat-reply-submit-${post.post_id}`} type="submit" className="bg-primary-accent text-white px-3 text-xs uppercase tracking-widest">
                <Send size={13} />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Chat() {
  const { active } = useTournament();
  const { user } = useAuth();
  const location = useLocation();
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState(null);
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [sort, setSort] = useState("top");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const tid = active?.tournament_id;

  useEffect(() => {
    if (!tid) return;
    (async () => {
      const { data } = await api.get(`/tournaments/${tid}/chat/rooms`);
      setRooms(data);
      // open a specific room if requested (e.g. from a notification)
      const params = new URLSearchParams(location.search);
      const qScope = params.get("scope");
      const qRoom = params.get("room_id");
      const match = qScope && qRoom ? data.find((r) => r.scope === qScope && r.room_id === qRoom) : null;
      setRoom(match || data[0] || null);
    })();
  }, [tid, location.search]);

  const loadPosts = useCallback(async () => {
    if (!tid || !room) return;
    setLoading(true);
    try {
      const [postsRes, memRes] = await Promise.all([
        api.get(`/tournaments/${tid}/chat`, { params: { scope: room.scope, room_id: room.room_id, sort } }),
        api.get(`/tournaments/${tid}/chat/members`, { params: { scope: room.scope, room_id: room.room_id } }),
      ]);
      setPosts(postsRes.data);
      setMembers(memRes.data);
    } finally {
      setLoading(false);
    }
  }, [tid, room, sort]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const memberNames = useMemo(() => members.map((m) => m.name), [members]);

  const flat = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      const key = p.parent_id || "__root__";
      (map[key] = map[key] || []).push(p);
    });
    return flattenTree(map, sort);
  }, [posts, sort]);

  const onVote = async (node, value) => {
    setPosts((ps) => ps.map((p) => p.post_id === node.post_id ? { ...p, my_vote: value, score: p.score - (p.my_vote || 0) + value } : p));
    try {
      const { data } = await api.post(`/chat/${node.post_id}/vote`, { value });
      setPosts((ps) => ps.map((p) => p.post_id === node.post_id ? { ...p, score: data.score, my_vote: data.my_vote } : p));
    } catch {
      toast.error("Vote failed");
      loadPosts();
    }
  };

  const createPost = async (content, parentId = null) => {
    try {
      const { data } = await api.post(`/tournaments/${tid}/chat`, { scope: room.scope, room_id: room.room_id, content, parent_id: parentId });
      setPosts((ps) => [...ps, data]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post");
    }
  };

  const submitTop = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await createPost(text.trim());
    setText("");
  };

  const canModerateRoom = !!room && (user?.role === "admin" || (room.scope === "group" && (user?.group_admin_ids || []).includes(room.room_id)));
  const canDelete = (post) => (post.user_id === user?.user_id && !post.is_system) || canModerateRoom;

  const onDelete = async (post) => {
    if (!window.confirm("Delete this post? Replies to it will also be removed.")) return;
    try {
      await api.delete(`/chat/${post.post_id}`);
      setPosts((ps) => ps.filter((p) => p.post_id !== post.post_id && p.parent_id !== post.post_id));
      toast.success("Post deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)]">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="text-primary-accent" size={22} />
          <h1 className="font-display text-3xl md:text-4xl uppercase tracking-tight">Chat Room</h1>
        </div>
        <p className="text-zinc-500 text-sm mb-5">Talk tactics, gloat, and argue. Upvote the takes you back.</p>

        <div className="flex flex-wrap gap-2 mb-4">
          {rooms.map((r) => {
            const isActive = room && room.scope === r.scope && room.room_id === r.room_id;
            return (
              <button
                key={`${r.scope}-${r.room_id}`}
                data-testid={`chat-room-${r.room_id}`}
                onClick={() => setRoom(r)}
                className={`px-3 py-2 text-xs uppercase tracking-widest border flex items-center gap-1.5 transition-colors ${
                  isActive ? "border-primary-accent text-white bg-primary-accent/10" : "border-zinc-800 text-zinc-400 hover:text-white"
                }`}
              >
                {r.scope === "tournament" ? <Hash size={12} /> : <Users size={12} />} {r.name}
              </button>
            );
          })}
        </div>

        <div className="surface-card p-4 mb-4">
          <form onSubmit={submitTop} className="flex gap-2">
            <MentionTextInput
              value={text}
              onChange={setText}
              onEnter={submitTop}
              members={members}
              testid="chat-composer-input"
              placeholder={room ? `Post to ${room.name}… use @ to mention` : "Loading…"}
            />
            <button data-testid="chat-post-submit-btn" type="submit" className="bg-primary-accent text-white px-5 font-display tracking-widest text-xs uppercase flex items-center gap-2">
              <Send size={14} /> Post
            </button>
          </form>
          <div className="flex items-center gap-2 mt-3">
            <span className="label-tag text-zinc-500">Sort</span>
            <button data-testid="chat-sort-top" onClick={() => setSort("top")} className={`text-xs uppercase tracking-widest flex items-center gap-1 px-2 py-1 ${sort === "top" ? "text-primary-accent" : "text-zinc-500"}`}>
              <Flame size={12} /> Top
            </button>
            <button data-testid="chat-sort-new" onClick={() => setSort("new")} className={`text-xs uppercase tracking-widest flex items-center gap-1 px-2 py-1 ${sort === "new" ? "text-primary-accent" : "text-zinc-500"}`}>
              <Clock size={12} /> New
            </button>
          </div>
        </div>

        <div className="surface-card divide-y divide-white/5 px-4">
          {loading && <div className="py-8 text-center text-zinc-500 text-sm">Loading…</div>}
          {!loading && flat.length === 0 && (
            <div className="py-10 text-center text-zinc-500" data-testid="chat-empty">
              No posts yet. Start the conversation!
            </div>
          )}
          {!loading && flat.map((item) => (
            <PostRow key={item.post.post_id} item={item} onVote={onVote} onReply={createPost} onDelete={onDelete} canDelete={canDelete(item.post)} members={members} memberNames={memberNames} />
          ))}
        </div>
      </div>
    </div>
  );
}
